<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("resources") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="computedPage"
				/>
			</template>
			<template slot="actions">
				<j-button @click="$modal.show('createModal')">
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("add") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_resource')"
					:value="searchTerm"
					@input="onSearch($event)"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
					>{{ $t("cancel") }}</j-button
				>
			</div>
		</div>

		<!-- Categories filter -->
		<j-card class="p4 mb4 bg5">
			<div class="fw7">{{ $t("categories") }}</div>
			<div class="flex wrap pb2">
				<div v-for="category in categories" :key="category">
					<div
						class="fs3 p2 r2 mr2 mt2 cu2"
						:class="[
							{ 'bg7 c1': !selectedCategories.includes(category) },
							{ 'bg2 c5': selectedCategories.includes(category) }
						]"
						@click="selectCategory(category)"
					>
						{{ category }}
					</div>
				</div>
			</div>
		</j-card>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'name'">
								<router-link
									:to="{
										name: `ResourceDetail`,
										params: { id: item.id }
									}"
									>{{ item.name ? item.name : "---" }}</router-link
								>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>

							<!-- Actions Field -->
							<template v-if="column.field === 'actions'">
								<div class="action">
									<!-- Delete -->
									<crm-icon
										name="delete"
										size="normal"
										color="cError"
										@click.native="showModal(item)"
									/>
								</div>
							</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems ? listing.numberOfItems : 0"
				:perPage="itemsPerPage"
				:activePage="computedPage"
				@click="onPageChange($event)"
			/>
		</div>

		<!-- Delete confirmation modal -->
		<j-dialog
			:height="200"
			:width="600"
			:title="$t('delete')"
			type="delete"
			@confirm="removeResource(selectedItem)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_resource") }}</j-dialog
		>

		<core-modal :headline="$t('create_resource')" name="createModal">
			<resource-create @resource:created="onResourceCreated" />
		</core-modal>
	</core-layout-content>
</template>

<script>
/*
 * Read resources/readme.md to understand why we use courses API and what are the differences with Education module
 */
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JDialog from "@/components/JDialog/"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import ResourceCreate from "../components/ResourceCreate"

import debounce from "lodash.debounce"
import queries from "../utils/queries.js"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		CoreModal,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JDialog,
		ResourceCreate
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					text: this.$t("categories"),
					field: "categories"
				},
				{
					text: this.$t("version"),
					field: "version"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				}
			],

			selectedItem: null,
			selectedCategories: [],
			categories: []
		}
	},

	computed: {
		/* Compute fetch data for data table */
		computedList() {
			if (!this.listing.list) return []

			return this.listing.list.map(item => ({
				id: item.id,
				name: item.name,
				description: item.description,
				version: item.version,
				categories: item?.categories?.join(", "),
				sections: item.sections,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null
			}))
		},

		computedPage() {
			return this.$store.state.education.listing.page
		},

		computedWhere() {
			return {
				and: [
					/* Resources are basically courses with the tag 'resource' */
					{
						categories_contains_some: ["resource"]
					},

					/* Filter with both categories and searchTerm  */
					this.searchTerm?.trim()?.length && {
						name_contains: this.searchTerm
					},
					this.selectedCategories?.length && {
						categories_contains_some: this.selectedCategories
					}
				].filter(w => w)
			}
		}
	},

	methods: {
		onPageChange(pageIndex) {
			this.$store.commit("education/SET_PAGE", pageIndex)
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		selectCategory(payload) {
			if (this.selectedCategories.includes(payload)) {
				this.selectedCategories = this.selectedCategories.filter(
					item => item !== payload
				)
			} else {
				this.selectedCategories.push(payload)
			}
			this.readAll()
		},

		onSearch: debounce(function(value) {
			this.searchTerm = value
			this.$store.commit("resources/SET_PAGE", 1)
			this.readAll()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.$store.commit("resources/SET_PAGE", 1)
			this.onSearch()
		},

		onResourceCreated() {
			this.$modal.hide("createModal")
			this.readAll()
		},

		async fetchCategories() {
			this.isLoading = true
			/* We only want the categories of the courses with tag 'resource', to avoid the courses from Education module */
			const variables = {
				where: {
					categories_contains_some: ["resource"]
				}
			}
			const res = await api({
				query: queries.fetchCategories,
				variables
			})
			this.isLoading = false

			// Check errors
			if (res.errors && res?.errors?.length)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			const categories = []
			res?.data?.courses?.list?.forEach(item => {
				item.categories?.forEach(category => {
					if (!categories.includes(category)) categories.push(category)
				})
			})

			// Do not show 'resource', we force this tag in the where anyway so it's useless in the filter
			this.categories = categories.filter(c => c !== "resource")
		},

		async readAll() {
			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.computedPage - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			const where = this.computedWhere

			this.isLoading = true
			const res = await api({
				query: queries.fetchCourses,
				variables: {
					skip,
					limit,
					where,
					sort
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors && res?.errors?.length)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.listing.list = res?.data?.courses?.list || []
			this.listing.numberOfItems = res?.data?.courses?.totalCount || 0
			this.fetchCategories()
		},

		showModal(resource) {
			this.selectedItem = resource
			this.$modal.show("j-dialog")
		},

		hideModal() {
			this.selectedItem = null
			this.$modal.hide("j-dialog")
		},

		async removeResource(item) {
			if (item?.sections?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.this_resource_is_not_empty")
					}
				})
				return
			}
			/*
			 * Delete resource
			 */
			let variables = {
				id: item.id
			}
			let res = await api({ query: queries.deleteCourse, variables })
			if (res?.data?.deleteCourse?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				return
			}
			/*
			 * Delete L10n
			 */
			variables = {
				key: `courses-${item.id}-name`
			}
			res = await api({ query: queries.deleteAllL10n, variables })
			variables = {
				key: `courses-${item.id}-description`
			}
			res = await api({ query: queries.deleteAllL10n, variables })
			this.hideModal()
			this.readAll()
		}
	},

	beforeRouteLeave(to, from, next) {
		// If in same module , do nothing, do not reset page
		if (to.meta.featureId === from.meta.featureId) {
			next()
		} else {
			// if not in same module, reset page to 1
			this.$store.commit("resources/SET_PAGE", 1)
			next()
		}
	},

	mounted() {
		this.readAll()
	}
}
</script>
